// Галерея фото с загрузкой и порядком. Первая картинка = обложка (cover).
// Можно загружать несколько файлов за раз, вставлять URL-ы, переставлять
// стрелками "← →", делать любую главной кнопкой "звезда", удалять.
// Клиент — предоптимизация через canvas; сервер — sharp → webp.
//
// Используется для фото блюда (Меню) и обложек карточки заведения в гостевом
// приложении («Приложение»).
import { useRef, useState } from 'react';
import { ArrowLeft, ArrowRight, Loader2, Plus, Star, Trash2, Upload } from 'lucide-react';
import toast from 'react-hot-toast';

import { uploadImage } from '@/services/admin';
import { asAbsoluteUrl } from '@/utils/url';
import { compressImageForUpload } from '@/utils/image';

type UploadKind = Parameters<typeof uploadImage>[0];

export function PhotosField({
  value,
  onChange,
  kind = 'dish',
  max = 10,
  aspect = 'square',
  coverLabel = 'Обложка',
  hint = 'JPG, PNG, WebP, HEIC — до 15 МБ каждое. Большие фото автоматически сжимаются.',
}: {
  value: string[];
  onChange: (photos: string[]) => void;
  /** Папка на сервере: dish → /uploads/dishes, cover → /uploads/covers. */
  kind?: UploadKind;
  max?: number;
  /** Пропорции плитки: квадрат (блюда) или 16:10 (обложки карточки). */
  aspect?: 'square' | 'wide';
  coverLabel?: string;
  hint?: string;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploadingCount, setUploadingCount] = useState(0);
  const [urlMode, setUrlMode] = useState(false);
  const [urlDraft, setUrlDraft] = useState('');

  const uploading = uploadingCount > 0;
  const limitLeft = Math.max(0, max - value.length);
  const tileClass = aspect === 'wide' ? 'aspect-[16/10]' : 'aspect-square';
  const gridClass =
    aspect === 'wide'
      ? 'grid grid-cols-[repeat(auto-fill,minmax(150px,1fr))] gap-2'
      : 'grid grid-cols-[repeat(auto-fill,minmax(110px,1fr))] gap-2';

  const handleFiles = async (files: FileList | File[] | null | undefined) => {
    if (!files) return;
    const incoming = Array.from(files).filter((f) => f.type.startsWith('image/'));
    if (!incoming.length) {
      toast.error('Поддерживаются только изображения');
      return;
    }
    const slots = Math.max(0, max - value.length);
    const toUpload = incoming.slice(0, slots);
    if (toUpload.length < incoming.length) {
      toast(`Максимум ${max} фото — лишние пропущены`);
    }
    if (!toUpload.length) return;

    setUploadingCount((n) => n + toUpload.length);
    const uploaded: string[] = [];
    let savedBytes = 0;
    try {
      const results = await Promise.allSettled(
        toUpload.map(async (f) => {
          const prepared = await compressImageForUpload(f);
          const res = await uploadImage(kind, prepared);
          savedBytes += Math.max(0, f.size - res.size);
          return res.url;
        }),
      );
      for (const r of results) {
        if (r.status === 'fulfilled') uploaded.push(r.value);
      }
      const failed = results.length - uploaded.length;
      if (uploaded.length) {
        onChange([...value, ...uploaded]);
      }
      if (failed > 0) toast.error(`${failed} файл(ов) не загрузилось`);
      if (uploaded.length && savedBytes > 20 * 1024) {
        toast.success(`Загружено ${uploaded.length} · сэкономили ~${formatBytes(savedBytes)}`);
      } else if (uploaded.length) {
        toast.success(`Загружено ${uploaded.length}`);
      }
    } finally {
      setUploadingCount((n) => Math.max(0, n - toUpload.length));
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const removeAt = (idx: number) => {
    const next = value.slice();
    next.splice(idx, 1);
    onChange(next);
  };
  const moveLeft = (idx: number) => {
    if (idx <= 0) return;
    const next = value.slice();
    [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
    onChange(next);
  };
  const moveRight = (idx: number) => {
    if (idx >= value.length - 1) return;
    const next = value.slice();
    [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
    onChange(next);
  };
  const makeCover = (idx: number) => {
    if (idx === 0) return;
    const next = value.slice();
    const [picked] = next.splice(idx, 1);
    next.unshift(picked);
    onChange(next);
  };

  const addUrl = () => {
    const url = urlDraft.trim();
    if (!url) return;
    if (value.length >= max) {
      toast.error(`Максимум ${max} фото`);
      return;
    }
    if (!/^https?:\/\//i.test(url) && !url.startsWith('/')) {
      toast.error('URL должен начинаться с http(s):// или /');
      return;
    }
    if (value.includes(url)) {
      toast.error('Это фото уже в галерее');
      return;
    }
    onChange([...value, url]);
    setUrlDraft('');
  };

  return (
    <div className="grid gap-3">
      <div
        onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
        onDrop={(e) => {
          e.preventDefault(); e.stopPropagation();
          if (uploading) return;
          handleFiles(e.dataTransfer.files);
        }}
        className={gridClass}
      >
        {value.map((url, idx) => (
          <PhotoTile
            key={`${url}-${idx}`}
            url={url}
            isCover={idx === 0}
            coverLabel={coverLabel}
            tileClass={tileClass}
            canMoveLeft={idx > 0}
            canMoveRight={idx < value.length - 1}
            onRemove={() => removeAt(idx)}
            onMoveLeft={() => moveLeft(idx)}
            onMoveRight={() => moveRight(idx)}
            onMakeCover={() => makeCover(idx)}
          />
        ))}
        {Array.from({ length: uploadingCount }).map((_, i) => (
          <div
            key={`up-${i}`}
            className={`${tileClass} rounded-lg border-2 border-dashed border-brand-300 bg-brand-50 flex items-center justify-center`}
          >
            <Loader2 size={20} className="animate-spin text-brand-500" />
          </div>
        ))}
        {limitLeft > 0 && !uploading && (
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            className={`${tileClass} rounded-lg border-2 border-dashed border-slate-200 hover:border-brand-300 hover:bg-slate-50 flex flex-col items-center justify-center text-slate-400 hover:text-brand-600 transition-colors`}
          >
            <Plus size={20} />
            <span className="text-[11px] mt-0.5">Добавить</span>
          </button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          className="btn-outline"
          disabled={uploading || limitLeft === 0}
          onClick={() => fileRef.current?.click()}
        >
          <Upload size={14} /> Загрузить
        </button>
        <button
          type="button"
          className="btn-ghost"
          onClick={() => setUrlMode((v) => !v)}
        >
          {urlMode ? 'Скрыть URL' : 'Вставить URL'}
        </button>
        <span className="text-xs text-slate-400 ml-auto">
          {value.length}/{max} · первая = {coverLabel.toLowerCase()}
        </span>
      </div>

      {urlMode && (
        <div className="flex gap-2">
          <input
            className="input flex-1"
            placeholder="https://…"
            value={urlDraft}
            onChange={(e) => setUrlDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addUrl(); } }}
          />
          <button type="button" className="btn-primary" onClick={addUrl} disabled={!urlDraft.trim()}>
            Добавить
          </button>
        </div>
      )}

      {hint && <p className="text-xs text-slate-400">{hint}</p>}

      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
      />
    </div>
  );
}

function PhotoTile({
  url,
  isCover,
  coverLabel,
  tileClass,
  canMoveLeft,
  canMoveRight,
  onRemove,
  onMoveLeft,
  onMoveRight,
  onMakeCover,
}: {
  url: string;
  isCover: boolean;
  coverLabel: string;
  tileClass: string;
  canMoveLeft: boolean;
  canMoveRight: boolean;
  onRemove: () => void;
  onMoveLeft: () => void;
  onMoveRight: () => void;
  onMakeCover: () => void;
}) {
  const src = asAbsoluteUrl(url) ?? url;
  return (
    <div className={`relative group ${tileClass} rounded-lg overflow-hidden border border-slate-200 bg-slate-100`}>
      <img
        src={src}
        alt=""
        className="w-full h-full object-cover"
        onError={(e) => { (e.currentTarget as HTMLImageElement).style.visibility = 'hidden'; }}
      />
      {isCover && (
        <span className="absolute top-1 left-1 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-400/95 text-amber-900 font-semibold shadow-sm">
          {coverLabel}
        </span>
      )}
      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/35 transition-colors" />
      <div className="absolute inset-x-0 bottom-0 p-1 flex items-center justify-between opacity-0 group-hover:opacity-100 transition-opacity">
        <div className="flex gap-1">
          <button
            type="button"
            title="Влево"
            disabled={!canMoveLeft}
            onClick={onMoveLeft}
            className="w-6 h-6 rounded bg-white/90 text-slate-700 hover:bg-white disabled:opacity-40 flex items-center justify-center"
          >
            <ArrowLeft size={12} />
          </button>
          <button
            type="button"
            title="Вправо"
            disabled={!canMoveRight}
            onClick={onMoveRight}
            className="w-6 h-6 rounded bg-white/90 text-slate-700 hover:bg-white disabled:opacity-40 flex items-center justify-center"
          >
            <ArrowRight size={12} />
          </button>
          {!isCover && (
            <button
              type="button"
              title={`Сделать первой (${coverLabel.toLowerCase()})`}
              onClick={onMakeCover}
              className="w-6 h-6 rounded bg-white/90 text-amber-600 hover:bg-white flex items-center justify-center"
            >
              <Star size={12} />
            </button>
          )}
        </div>
        <button
          type="button"
          title="Удалить"
          onClick={onRemove}
          className="w-6 h-6 rounded bg-white/90 text-rose-600 hover:bg-white flex items-center justify-center"
        >
          <Trash2 size={12} />
        </button>
      </div>
    </div>
  );
}

function formatBytes(bytes: number): string {
  if (bytes > 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} МБ`;
  return `${Math.round(bytes / 1024)} КБ`;
}
